import { Injectable } from '@angular/core';
import { StepType, SystemModuleType, WorkflowType } from '@ca/core/models';
import { environment } from '@ca/environment/environment';
import { Store } from '@ngrx/store';
import { Guid } from 'guid-typescript';
import { addUrlsToDefineInputModule, determineDefineInputConnectionType, getLatestAvailableModuleVersion } from '../../../utils/workflow/domain-utils';
import * as selectors from '../../app-store/common-store/common.selectors';
import { CommonState } from '../../app-store/common-store/common.state';
import { ModuleV1Read, VersionReadModel } from '../models/module-v1-read.model';
import { ModuleVersion } from '../models/module-version.model';
import { RunStep } from '../models/run-step.model';
import { Step } from '../models/step.model';
import { Workflow } from '../models/workflow.model';

@Injectable({
    providedIn: 'root',
})
export class DomainFactoryService {
    workflowDefineInputModule?: ModuleV1Read;
    moduleDefineInputModule?: ModuleV1Read;

    get systemModules(): ModuleV1Read[] | undefined {
        let systemModules: ModuleV1Read[] | undefined = undefined;

        if (this.moduleDefineInputModule != null || this.workflowDefineInputModule != null) {
            systemModules = [];
            if (this.moduleDefineInputModule != null) {
                systemModules.push(this.moduleDefineInputModule);
            }
            if (this.workflowDefineInputModule != null) {
                systemModules.push(this.workflowDefineInputModule);
            }
        }

        return systemModules;
    }

    constructor(private store: Store<CommonState>) {
        this.subscribeToSystemModulesFromStore();
    }

    isModuleDefineInput(moduleId?: string): boolean {
        let isModuleDefineInput = false;

        if (this.moduleDefineInputModule != null && moduleId != null) {
            isModuleDefineInput = this.moduleDefineInputModule.id === moduleId;
        }

        return isModuleDefineInput;
    }

    isWorkflowDefineInput(moduleId?: string): boolean {
        let isWorkflowDefineInput = false;

        if (this.workflowDefineInputModule != null && moduleId != null) {
            isWorkflowDefineInput = this.workflowDefineInputModule.id === moduleId;
        }

        return isWorkflowDefineInput;
    }

    createModuleStepFromModule(module: ModuleV1Read, flowId?: string, indexInWorkflow?: number): Step {
        const moduleVersionModel: VersionReadModel = getLatestAvailableModuleVersion(module);

        const stepType: StepType = this.determineSystemModuleStepType(module.id!, indexInWorkflow);

        return this.createModuleStepFromModuleVersion(moduleVersionModel, module, flowId, stepType);
    }

    createModuleStepFromModuleVersion(moduleVersionModel: VersionReadModel, module: ModuleV1Read, flowId?: string, stepType?: StepType): Step {
        const moduleVersion = new ModuleVersion(moduleVersionModel, module);
        return this.createModuleStep(moduleVersion, flowId, stepType);
    }

    createModuleStep(moduleVersion: ModuleVersion, flowId?: string, stepType: StepType = StepType.Module): Step {
        flowId = flowId != null ? flowId : Guid.create().toString();

        return new Step(flowId, moduleVersion, stepType);
    }

    findFirstModuleDefineInputStep(runSteps: RunStep[]): RunStep | undefined {
        return runSteps.find((step: RunStep) => step.module.systemModuleType != null && step.module.systemModuleType === SystemModuleType.DefineModuleInput);
    }

    findNthModuleDefineInputStep(runSteps: RunStep[], n: number): RunStep | undefined {
        const moduleDefineInputSteps: RunStep[] = runSteps.filter(
            (step: RunStep) => step.module.systemModuleType != null && step.module.systemModuleType === SystemModuleType.DefineModuleInput,
        );

        let targetStep: RunStep | undefined = undefined;
        if (moduleDefineInputSteps.length > n) {
            targetStep = moduleDefineInputSteps[n];
        }

        return targetStep;
    }

    findMLDataPreparationStep(runSteps: RunStep[]): RunStep | undefined {
        let dataPreparationModuleStringIds: string[] = [];
        const dataPreparationModuleIds = environment.machineLearningConfig != null ? environment.machineLearningConfig.dataPreparationModuleIds : undefined;
        if (dataPreparationModuleIds != null && dataPreparationModuleIds.length > 0) {
            dataPreparationModuleStringIds = dataPreparationModuleIds.map(dataPreparationModuleId => dataPreparationModuleId.toString());
        }
        return runSteps.find((step: RunStep) => step.module.id != null && dataPreparationModuleStringIds.indexOf(step.module.id) > -1);
    }

    findMLDatasetValidationStep(runSteps: RunStep[]): RunStep | undefined {
        let datasetValidationModuleStringIds: string[] = [];
        const datasetValidationModuleIds = environment.machineLearningConfig != null ? environment.machineLearningConfig.datasetValidationModuleIds : undefined;
        if (datasetValidationModuleIds != null && datasetValidationModuleIds.length > 0) {
            datasetValidationModuleStringIds = datasetValidationModuleIds.map(datasetValidationModuleId => datasetValidationModuleId.toString());
        }
        return runSteps.find((step: RunStep) => step.module.id != null && datasetValidationModuleStringIds.indexOf(step.module.id) > -1);
    }

    findApeerMlStep(runSteps: RunStep[]): RunStep | undefined {
        let apeerMlModuleStringIds: string[] = [];
        const apeerMlModuleIds = environment.machineLearningConfig != null ? environment.machineLearningConfig.apeerMlModuleIds : undefined;
        if (apeerMlModuleIds != null && apeerMlModuleIds.length > 0) {
            apeerMlModuleStringIds = apeerMlModuleIds.map(apeerMlModuleId => apeerMlModuleId.toString());
        }
        return runSteps.find((step: RunStep) => step.module.id != null && apeerMlModuleStringIds.indexOf(step.module.id) > -1);
    }

    createModuleDefineInputStep(): Step {
        const moduleDefineInputModule = this.getModuleDefineInputModule();

        if (moduleDefineInputModule == null) {
            throw new Error('A Module Define Input Module was not found. Please check!');
        }

        const moduleVersionModel = getLatestAvailableModuleVersion(moduleDefineInputModule);
        const moduleVersion: ModuleVersion = new ModuleVersion(moduleVersionModel, moduleDefineInputModule);

        return this.createModuleStep(moduleVersion, undefined, StepType.ModuleDefineInput);
    }

    createDefineInputStep(flowId?: string, defineInputModule?: ModuleV1Read): Step {
        if (defineInputModule == null) {
            defineInputModule = this.getWorkflowDefineInputModule();
        }

        if (defineInputModule == null) {
            throw new Error('A Define Input Module was not found. Please check!');
        }

        const moduleVersionModel = getLatestAvailableModuleVersion(defineInputModule);
        const moduleVersion: ModuleVersion = new ModuleVersion(moduleVersionModel, defineInputModule);

        return this.createModuleStep(moduleVersion, flowId, StepType.WorkflowDefineInput);
    }

    createNewWorkflowFromModuleWithDefineInput(module?: ModuleV1Read, shouldUseTestFiles: boolean = false): Workflow {
        const workflowModel: Workflow = this.createNewWorkflowWithDefineInput();

        if (module != null) {
            const additionalModuleStep: Step = this.createModuleStepFromModule(module);
            workflowModel.appendStep(additionalModuleStep);
            workflowModel.connectStepsIfPossible(0, 1);

            if (shouldUseTestFiles && module.latestPublicVersion != null && module.latestPublicVersion.testFileUrls != null) {
                const testFileUrls = module.latestPublicVersion.testFileUrls;
                const defineInputStep = workflowModel.steps[0];

                const workflowDefineInputConnectedType: 'file' | 'file_list' | undefined = determineDefineInputConnectionType(defineInputStep, workflowModel);
                addUrlsToDefineInputModule(defineInputStep, testFileUrls, workflowDefineInputConnectedType);
            }
        }

        return workflowModel;
    }

    createNewWorkflowWithDefineInput(): Workflow {
        const workflowModel: Workflow = new Workflow({
            id: '',
            title: '',
            description: '',
            usedModuleVersions: [],
            usedModules: [],
            visibility: 'Draft',
            type: WorkflowType.UNDEFINED,
            segmentationTypeSupported: undefined,
        });

        const defineInputStep = this.createDefineInputStep();
        workflowModel.appendStep(defineInputStep);

        return workflowModel;
    }

    getModuleDefineInputModule(): ModuleV1Read | undefined {
        return this.getSystemModule(SystemModuleType.DefineModuleInput);
    }

    getSystemModule(type: SystemModuleType): ModuleV1Read | undefined {
        if (type === SystemModuleType.DefineWorkflowInput) {
            return this.workflowDefineInputModule;
        }

        if (type === SystemModuleType.DefineModuleInput) {
            return this.moduleDefineInputModule;
        }
    }

    private subscribeToSystemModulesFromStore() {
        this.store.select(selectors.selectSystemModules).subscribe((systemModules: ModuleV1Read[]) => {
            this.workflowDefineInputModule = systemModules.find((m: ModuleV1Read) => m.systemModuleType === SystemModuleType.DefineWorkflowInput);
            this.moduleDefineInputModule = systemModules.find((m: ModuleV1Read) => m.systemModuleType === SystemModuleType.DefineModuleInput);
        });
    }

    private determineSystemModuleStepType(moduleId: string, indexInWorkflow?: number): StepType {
        let stepType: StepType = StepType.Module;

        if (indexInWorkflow != null && indexInWorkflow === 0) {
            const workflowDefineInputModule: ModuleV1Read | undefined = this.getWorkflowDefineInputModule();
            if (workflowDefineInputModule != null && workflowDefineInputModule.id === moduleId) {
                stepType = StepType.WorkflowDefineInput;
            }
        }

        const moduleDefineInputModule: ModuleV1Read | undefined = this.getModuleDefineInputModule();
        if (moduleDefineInputModule != null && moduleId === moduleDefineInputModule.id) {
            stepType = StepType.ModuleDefineInput;
        }

        return stepType;
    }

    private getWorkflowDefineInputModule(): ModuleV1Read | undefined {
        return this.getSystemModule(SystemModuleType.DefineWorkflowInput);
    }
}
